import type { $Fetch } from 'ofetch';
import type { ProtocoloWorkflowGateway } from '@/application/port/out/ProtocoloWorkflowGateway';
import type {
  Protocolo, ProtocoloMensagem, ProtocoloTransferencia, ProtocoloAnexo,
  AbrirProtocoloCommand, ResponderProtocoloCommand,
  EncerrarProtocoloCommand, TransferirProtocoloCommand,
} from '@/domain/model/Protocolo';
import type { ApiResponse } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

/**
 * HTTP gateway do workflow admin de Protocolo (acadadmin).
 * Root `/api/v1/protocolos`; anexos `/api/v1/protocolos/mensagens/{mensagemId}/anexos` (multipart).
 *
 * @since G.5.16
 */
export class HttpProtocoloWorkflowGateway implements ProtocoloWorkflowGateway {
  private readonly base = '/api/v1/protocolos';
  constructor(private readonly client: $Fetch) {}

  async get(id: string): Promise<Protocolo> {
    const res = await this.client<ApiResponse<Protocolo>>(`${this.base}/${encodeURIComponent(id)}`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }
  async thread(id: string): Promise<ProtocoloMensagem[]> {
    const res = await this.client<ApiResponse<ProtocoloMensagem[]>>(
      `${this.base}/${encodeURIComponent(id)}/mensagens`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }
  async transferencias(id: string): Promise<ProtocoloTransferencia[]> {
    const res = await this.client<ApiResponse<ProtocoloTransferencia[]>>(
      `${this.base}/${encodeURIComponent(id)}/transferencias`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }
  async abrir(cmd: AbrirProtocoloCommand): Promise<Protocolo> {
    const res = await this.client<ApiResponse<Protocolo>>(this.base, { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }
  async responder(id: string, cmd: ResponderProtocoloCommand): Promise<Protocolo> {
    const res = await this.client<ApiResponse<Protocolo>>(
      `${this.base}/${encodeURIComponent(id)}/responder`, { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }
  async encerrar(id: string, cmd: EncerrarProtocoloCommand): Promise<Protocolo> {
    const res = await this.client<ApiResponse<Protocolo>>(
      `${this.base}/${encodeURIComponent(id)}/encerrar`, { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }
  async reabrir(id: string): Promise<Protocolo> {
    const res = await this.client<ApiResponse<Protocolo>>(
      `${this.base}/${encodeURIComponent(id)}/reabrir`, { method: 'POST' });
    assertApiSuccess(res);
    return res.data;
  }
  async transferir(id: string, cmd: TransferirProtocoloCommand): Promise<Protocolo> {
    const res = await this.client<ApiResponse<Protocolo>>(
      `${this.base}/${encodeURIComponent(id)}/transferir`, { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }
  async listAnexos(mensagemId: string): Promise<ProtocoloAnexo[]> {
    const res = await this.client<ApiResponse<ProtocoloAnexo[]>>(
      `${this.base}/mensagens/${encodeURIComponent(mensagemId)}/anexos`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }
  async uploadAnexo(mensagemId: string, file: File): Promise<ProtocoloAnexo> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await this.client<ApiResponse<ProtocoloAnexo>>(
      `${this.base}/mensagens/${encodeURIComponent(mensagemId)}/anexos`, { method: 'POST', body: formData });
    assertApiSuccess(res);
    return res.data;
  }
}
